/// <reference types="@types/google.maps" />
import { inject, Injectable } from '@angular/core';
import { catchAndLogError } from '@rma/common-ui-rxjs/operators';
import { BaseAddress } from '@rma/generic/domain/types';
import { Environment } from '@rma/generic/util/environment';
import { set } from 'lodash-es';
import { from, map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class GoogleMapsAutoCompleteSuggestionService {
  private readonly environment = inject(Environment);

  readonly googleMappings: Record<string, string> = {
    subpremise: 'unitNumber',
    street_number: 'streetNumber',
    route: 'street',
    administrative_area_level_1: 'state',
    country: 'country',
    postal_code: 'postcode',
  };

  private geocoder: google.maps.Geocoder | undefined;

  search(input: string): Observable<google.maps.places.PlacePrediction[]> {
    const sessionToken = new google.maps.places.AutocompleteSessionToken();

    const request: google.maps.places.AutocompleteRequest = {
      includedRegionCodes: [this.environment.countryCode],
      includedPrimaryTypes: ['street_address'],
      input,
      sessionToken,
    };

    return from(google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)).pipe(
      catchAndLogError<{ suggestions: google.maps.places.AutocompleteSuggestion[] }>(of({ suggestions: [] })),
      map(({ suggestions }) => suggestions.map(({ placePrediction }) => placePrediction).filter((x) => x !== null)),
    );
  }

  mapPredictionToType<T extends BaseAddress>(results: google.maps.GeocoderResult[]): T | undefined {
    const result = results[0];

    if (!result?.address_components) {
      return;
    }

    const address = {
      locality: result.address_components[2].long_name,
      coords: result.geometry ? { latitude: result.geometry.location.lat(), longitude: result.geometry.location.lng() } : undefined,
    };

    result.address_components.forEach((component) => {
      component.types.forEach((type) => {
        if (Object.prototype.hasOwnProperty.call(this.googleMappings, type)) {
          set(address, this.googleMappings[type], component.short_name);
        }
      });
    });

    return address as T;
  }

  addressToGeocoder(address: string): Observable<google.maps.GeocoderResult[]> {
    const request: google.maps.GeocoderRequest = {
      address,
    };

    return new Observable((obs) => {
      const callback = (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        switch (status) {
          case google.maps.GeocoderStatus.ZERO_RESULTS:
            obs.next([]);
            obs.complete();
            break;

          case google.maps.GeocoderStatus.OK:
            obs.next(results ?? []);
            obs.complete();
            break;

          default:
            obs.error(status);
            break;
        }
      };

      const service = (this.geocoder ||= new google.maps.Geocoder());
      service.geocode(request, callback);
    });
  }
}
